import {
  Button,
  TextField,
  Checkbox,
  AppHeader,
  Typography,
  Alert,
  Modal,
  ModalActionButton,
  ModalActions,
  ModalContent,
  ModalTitle,
  ModalCloseIconButton,
  useTextValidator,
  useZipCodeValidator,
  useStateInitialsValidator,
  Form,
  Container,
} from '@ace-clublabs/shared-component-library';
import React, { useContext, useEffect, useState, useLayoutEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { Address } from '@lib/types';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { ConfirmZipCodeModal } from '../components/ConfirmZipCodeModal';
import { getNextPage } from '../utils/getNextPage';
import { UtagType } from '../utils/UtagHelper';
import { ClubContentName, getClubContent } from '../utils/getClubContent';
import { AddressCompareModal } from '../components/AddressCompareModal';
import { getProgressProps } from '../utils/helpers';
import { AppProgressHeader } from '../components/AppProgressHeader';

interface ICorrectedResidence {
  address?: Address;
}

//Address page for donor here
export const GiftDonorAddressPage: React.FC = () => {
  const {
    sessionData,
    submitMembershipForm,
    loadingProvider,
    loadingInfo,
    membershipInfo,
    refreshSession,
    sendTag,
    updateMembershipForm,
    getNewBusinessCost,
    handleZipCodeChange,
  } = useContext(MembershipAppStateContext);

  const { formData } = sessionData;
  const history = useHistory();
  const [isTagging, setIsTagging] = useState(true);
  const [serverValidationErrors, setServerValidationErrors] = useState({ hasError: false, error: '' });
  const [correctedResidence, setCorrectedResidence] = useState<ICorrectedResidence | null>(null);
  const [showAddressCorrectionModal, setShowAddressCorrectionModal] = useState(false);
  const [showAddressSameModal, setShowAddressSameModal] = useState(false);
  const [acceptedAddressChanges, setAcceptedAddressChanges] = useState(false);
  const [showConfirmZipCodeModal, setShowConfirmZipCodeModal] = useState(false);
  const isAddressCorrected = correctedResidence != null;
  // set and modified when mailing address checkbox is clicked so new form renders without internal state
  const [formKey, setFormKey] = useState(new Date().toISOString());
  const clubCode = sessionData?.clubCode || '004';
  const textValidator = useTextValidator();
  const zipCodeValidator = useZipCodeValidator();
  const stateValidator = useStateInitialsValidator();
  const phoneNumber = getClubContent(ClubContentName.ContactPhoneText, clubCode);

  const progressProps = getProgressProps(sessionData.flowType!, Pages.GiftDonorAddressPage);

  const getInitialAddressState = (address: Address | undefined): Address => {
    return {
      streetAddress: address?.streetAddress || '',
      city: address?.city || '',
      state: address?.state || '',
      zipCode: address?.zipCode?.slice(0, 5) || '',
    };
  };

  const [donorResidenceAddressForm, setDonorResidenceAddressFormData] = useState(
    getInitialAddressState(formData.giftDetails?.donor?.residenceAddress)
  );
  const [hasDonorMailingAddress, setDonorHasMailingAddress] = useState(
    formData.giftDetails?.donor?.mailingAddress != undefined &&
      formData.giftDetails?.donor?.mailingAddress.streetAddress !== '' &&
      formData.giftDetails?.donor?.mailingAddress.city !== '' &&
      formData.giftDetails?.donor?.mailingAddress.state !== '' &&
      formData.giftDetails?.donor?.mailingAddress.zipCode !== ''
  );
  const [donorMailingAddressForm, setDonorMailingAddressFormData] = useState(
    getInitialAddressState(formData.giftDetails?.donor?.mailingAddress)
  );

  useEffect(() => {
    const updatedResidenceFormData = {
      ...sessionData.formData,
      giftDetails: {
        ...sessionData.formData.giftDetails,
        donor: {
          ...sessionData.formData?.giftDetails?.donor,
          residenceAddress: donorResidenceAddressForm,
        },
      },
    };
    updateMembershipForm(updatedResidenceFormData);
  }, [donorResidenceAddressForm]);

  useEffect(() => {
    const updatedMailingAddressFormData = {
      ...sessionData.formData,
      giftDetails: {
        ...sessionData.formData.giftDetails,
        donor: {
          ...sessionData.formData?.giftDetails?.donor,
          mailingAddress: donorMailingAddressForm,
        },
      },
    };
    updateMembershipForm(updatedMailingAddressFormData);
  }, [donorMailingAddressForm]);

  useEffect(() => {
    if (acceptedAddressChanges) {
      setAcceptedAddressChanges(false);
      submitForm({ skipDonorAddressValidation: true });
    }
  }, [sessionData]);

  useEffect(() => {
    refreshSession();
  }, []);

  useLayoutEffect(() => {
    if (!membershipInfo?.benefits) {
      return;
    }

    sendTag(UtagType.view, 'membership:gift:donor address');
    setIsTagging(false);
  }, [membershipInfo]);

  const navigateToNextPage = async () => {
    loadingProvider.startLoading('Saving your membership information');
    await getNewBusinessCost();
    loadingProvider.endLoading();
    history.push(getNextPage(Pages.GiftDonorAddressPage, sessionData.flowType));
  };

  const submitForm = async (props?: { skipDonorAddressValidation?: boolean }) => {
    try {
      loadingProvider.startLoading('Saving your membership information');
      setServerValidationErrors({ hasError: false, error: '' });
      const response = await submitMembershipForm(true, props?.skipDonorAddressValidation);
      if (isSameAddress()) {
        setShowAddressSameModal(true);
        loadingProvider.endLoading();
      } else if (response?.donorAddressValidation?.status === 'CORRECTED') {
        const correction = {} as ICorrectedResidence;
        correction.address = {
          streetAddress: response.donorAddressValidation.correctedAddress?.addressLine1 ?? '',
          city: response.donorAddressValidation.correctedAddress?.city ?? '',
          state: response.donorAddressValidation.correctedAddress?.state ?? '',
          zipCode: `${response.donorAddressValidation.correctedAddress?.zip} - ${response.donorAddressValidation.correctedAddress?.zipSuffix}`,
        };
        setCorrectedResidence(correction);
        setShowAddressCorrectionModal(true);
        loadingProvider.endLoading();
      } else if (response?.donorAddressValidation?.status === 'INVALID') {
        setShowAddressCorrectionModal(true);
        loadingProvider.endLoading();
      } else {
        navigateToNextPage();
      }
    } catch (e) {
      const isBotDetectionResponse =
        // @ts-ignore
        e?.error?.response?.status === 403 && !!e.error?.response?.data?.pxEvent;
      if (isBotDetectionResponse) {
        loadingProvider.endLoading();
        window._pxOnCaptchaSuccess = function (isValid) {
          if (isValid) {
            submitForm(props);
          }
        };
      } else {
        history.push(Pages.SorryPage);
      }
    }
  };
  const isSameAddress = () => {
    //compare addresses
    const donor = sessionData.formData.giftDetails?.donor;
    const donorAddress = sessionData.formData.giftDetails?.donor?.residenceAddress;
    const form = sessionData.formData;
    const primary = sessionData.formData.residenceAddress;

    if (
      donor?.firstName?.toLowerCase().trim() == form.firstName?.toLowerCase().trim() &&
      donor?.lastName?.toLowerCase().trim() == form.lastName?.toLowerCase().trim() &&
      donorAddress?.streetAddress?.toLowerCase().trim() == primary.streetAddress?.toLowerCase().trim() &&
      donorAddress?.city?.toLowerCase().trim() == primary.city?.toLowerCase().trim() &&
      donorAddress?.state?.toLowerCase().trim() == primary.state?.toLowerCase().trim() &&
      donorAddress?.zipCode?.toLowerCase().trim() == primary.zipCode?.toLowerCase().trim()
    ) {
      return true;
    } else {
      return false;
    }
  };

  const AddressSameModal = () => {
    function redirectToNamePage(): void {
      setShowAddressSameModal(false);
      history.push(Pages.GiftDonorContactPage);
    }

    return (
      <Modal open={showAddressSameModal} onClose={() => setShowAddressSameModal(false)}>
        <ModalTitle
          id="address-modal-title"
          sx={{
            fontSize: '20px !important',
            marginRight: '24px !important',
            fontWeight: '700',
            marginBottom: '0 !important',
          }}
        >
          The gift giver cannot be the same as the gift recipient
          <ModalCloseIconButton aria-label="Close" onClick={() => setShowAddressSameModal(false)} />
        </ModalTitle>

        <ModalContent id={'modal-content-description'}>
          <Typography variant="b2Regular">
            Your name and address cannot be the same as the gift recipient's. Please call {phoneNumber} for assistance.
          </Typography>
        </ModalContent>

        <ModalActions
          actionLayout={{
            sm: 'shared',
            md: 'shared',
            lg: 'shared',
            xl: 'shared',
          }}
        >
          <ModalActionButton
            variant="contained"
            id="changeAddressButton"
            label="Change address"
            onClick={() => {
              // Add your confirmation logic here
              setShowAddressSameModal(false);
            }}
          />
          <ModalActionButton
            variant="outlined"
            id="changeNameButton"
            label="Change name"
            onClick={() => redirectToNamePage()}
          />
        </ModalActions>
      </Modal>
    );
  };
  const isLoading = loadingInfo.isLoading || membershipInfo?.benefits == null;
  if (isLoading) {
    return (
      <div>
        <AppHeader sx={{ padding: 0 }} />
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={0} currentStep={loadingInfo.loadingDetails.length} />
      </div>
    );
  }

  if (isTagging) {
    return <></>;
  }

  return (
    <>
      <AppProgressHeader {...progressProps} styleOverrides={{ marginBottom: '1rem' }} />

      {loadingInfo.isLoading ? (
        <LoadingIndicator loadingInfo={loadingInfo} maxSteps={2} currentStep={loadingInfo.loadingDetails.length} />
      ) : (
        <Container>
          <div className="contact-details-header-container">
            <Typography variant="h5" className="contact-details-header-title">
              What is the gift giver's address
            </Typography>
            {serverValidationErrors?.hasError ? (
              <Alert
                children={
                  <Typography className="validation-error-alert" variant="b3Regular">
                    {serverValidationErrors?.error}
                  </Typography>
                }
                severity="error"
                displayCloseButton={false}
              />
            ) : showAddressCorrectionModal ? (
              <AddressCompareModal
                isOpen={showAddressCorrectionModal}
                onClose={() => {
                  setCorrectedResidence(null);
                  setShowAddressCorrectionModal(false);
                }}
                isAddressCorrected={isAddressCorrected}
                addressForm={donorResidenceAddressForm}
                correctedAddress={correctedResidence}
                acceptCorrectionClick={() => {
                  setDonorResidenceAddressFormData((prev) => ({
                    ...prev,
                    streetAddress: correctedResidence?.address?.streetAddress ?? '',
                    city: correctedResidence?.address?.city ?? '',
                    state: correctedResidence?.address?.state ?? '',
                    zipCode: correctedResidence?.address?.zipCode ?? '',
                  }));

                  setCorrectedResidence(null);
                  setShowAddressCorrectionModal(false);
                  setAcceptedAddressChanges(true);
                }}
                navigateToNextPage={() => navigateToNextPage()}
              />
            ) : showAddressSameModal ? (
              <>{AddressSameModal()}</>
            ) : (
              <></>
            )}
          </div>

          <Form key={formKey} sx={{ marginTop: 0 }}>
            <div className="address-form qm-mask">
              <TextField
                value={donorResidenceAddressForm.streetAddress}
                label="Address"
                id="streetAddress"
                name="streetAddress"
                fullWidth
                required
                validator={textValidator}
                showClearButton={true}
                onChange={(v) => {
                  setDonorResidenceAddressFormData((prev) => ({ ...prev, streetAddress: v.currentTarget.value }));
                }}
                onClearButtonClick={() => {
                  setDonorResidenceAddressFormData((prev) => ({ ...prev, streetAddress: '' }));
                }}
                sx={{
                  '& .MuiFormLabel-asterisk': {
                    display: 'none',
                  },
                }}
              />
              <div className="city-state-container">
                <div className="address-page-small-input-box">
                  <TextField
                    value={donorResidenceAddressForm.city}
                    label="City"
                    id="city"
                    name="city"
                    fullWidth
                    required
                    validator={textValidator}
                    showClearButton={true}
                    onChange={(v) => {
                      setDonorResidenceAddressFormData((prev) => ({ ...prev, city: v.currentTarget.value }));
                    }}
                    onClearButtonClick={() => {
                      setDonorResidenceAddressFormData((prev) => ({ ...prev, city: '' }));
                    }}
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        display: 'none',
                      },
                    }}
                  />
                </div>
                <div className="address-page-small-input-box">
                  <TextField
                    value={donorResidenceAddressForm.state}
                    label="State"
                    id="state"
                    name="state"
                    fullWidth
                    required
                    validator={stateValidator}
                    showClearButton={true}
                    onChange={(v) => {
                      setDonorResidenceAddressFormData((prev) => ({
                        ...prev,
                        state: v.currentTarget.value.toUpperCase(),
                      }));
                    }}
                    onClearButtonClick={() => {
                      setDonorResidenceAddressFormData((prev) => ({ ...prev, state: '' }));
                    }}
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        display: 'none',
                      },
                    }}
                  />
                </div>
              </div>
              <div className="address-page-small-input-box zip-input-container">
                <TextField
                  value={donorResidenceAddressForm.zipCode}
                  label="ZIP code"
                  id="zipCode"
                  name="zipCode"
                  fullWidth
                  required
                  validator={zipCodeValidator}
                  showClearButton={true}
                  onChange={(v) => {
                    setDonorResidenceAddressFormData((prev) => ({ ...prev, zipCode: v.currentTarget.value }));
                  }}
                  onClearButtonClick={() => {
                    setDonorResidenceAddressFormData((prev) => ({ ...prev, zipCode: '' }));
                  }}
                  sx={{
                    '& .MuiFormLabel-asterisk': {
                      display: 'none',
                    },
                  }}
                />
              </div>
            </div>

            <div className="check-items" id="checkbox-align">
              <Checkbox
                name="check-different-mailing"
                label="Mailing address is different"
                id="check-different-mailing"
                variant="text"
                checked={hasDonorMailingAddress}
                onChange={(e) => {
                  setDonorHasMailingAddress(e.checked);
                  setFormKey(new Date().toISOString());
                  if (!e.checked) {
                    setDonorMailingAddressFormData(() => ({
                      streetAddress: '',
                      city: '',
                      state: '',
                      zipCode: '',
                    }));
                  }
                }}
                fullWidth
              ></Checkbox>
            </div>

            {hasDonorMailingAddress && (
              <div className="mailing-address-form qm-mask">
                <TextField
                  value={donorMailingAddressForm.streetAddress ?? ''}
                  label="Address"
                  id="mailingStreetAddress"
                  name="mailingStreetAddress"
                  fullWidth
                  required={hasDonorMailingAddress}
                  validator={textValidator}
                  showClearButton={true}
                  onChange={(v) => {
                    setDonorMailingAddressFormData((prev) => ({ ...prev, streetAddress: v.currentTarget.value }));
                  }}
                  onClearButtonClick={() => {
                    setDonorMailingAddressFormData((prev) => ({ ...prev, streetAddress: '' }));
                  }}
                  sx={{
                    '& .MuiFormLabel-asterisk': {
                      display: 'none',
                    },
                  }}
                />
                <div className="city-state-container">
                  <div className="address-page-small-input-box">
                    <TextField
                      value={donorMailingAddressForm.city ?? ''}
                      label="City"
                      id="mailingCity"
                      name="mailingCity"
                      fullWidth
                      required={hasDonorMailingAddress}
                      validator={textValidator}
                      showClearButton={true}
                      onChange={(v) => {
                        setDonorMailingAddressFormData((prev) => ({ ...prev, city: v.currentTarget.value }));
                      }}
                      onClearButtonClick={() => {
                        setDonorMailingAddressFormData((prev) => ({ ...prev, city: '' }));
                      }}
                      sx={{
                        '& .MuiFormLabel-asterisk': {
                          display: 'none',
                        },
                      }}
                    />
                  </div>
                  <div className="address-page-small-input-box">
                    <TextField
                      value={donorMailingAddressForm.state ?? ''}
                      label="State"
                      id="mailingState"
                      name="mailingState"
                      fullWidth
                      required={hasDonorMailingAddress}
                      validator={stateValidator}
                      showClearButton={true}
                      onChange={(v) => {
                        setDonorMailingAddressFormData((prev) => ({
                          ...prev,
                          state: v.currentTarget.value.toUpperCase(),
                        }));
                      }}
                      onClearButtonClick={() => {
                        setDonorMailingAddressFormData((prev) => ({ ...prev, state: '' }));
                      }}
                      sx={{
                        '& .MuiFormLabel-asterisk': {
                          display: 'none',
                        },
                      }}
                    />
                  </div>
                </div>
                <div className="address-page-small-input-box zip-input-container">
                  <TextField
                    value={donorMailingAddressForm.zipCode ?? ''}
                    label="ZIP code"
                    id="mailingZipCode"
                    name="mailingZipCode"
                    fullWidth
                    required={hasDonorMailingAddress}
                    validator={zipCodeValidator}
                    showClearButton={true}
                    onChange={(v) => {
                      setDonorMailingAddressFormData((prev) => ({ ...prev, zipCode: v.currentTarget.value }));
                    }}
                    onClearButtonClick={() => {
                      setDonorMailingAddressFormData((prev) => ({ ...prev, zipCode: '' }));
                    }}
                    sx={{
                      '& .MuiFormLabel-asterisk': {
                        display: 'none',
                      },
                    }}
                  />
                </div>
              </div>
            )}
            <div className="address-form-btn">
              <Button
                label="Next"
                fullWidth
                onClick={() => {
                  submitForm();
                }}
                sx={{ marginTop: '1.5rem' }}
                type="submit"
                variant="contained"
                size="medium"
                id="nextButton"
              ></Button>
            </div>
            <ConfirmZipCodeModal
              isOpen={showConfirmZipCodeModal}
              closeModal={() => {
                setShowConfirmZipCodeModal(false);
              }}
              confirm={() => {
                handleZipCodeChange();
                setShowConfirmZipCodeModal(false);
              }}
            />
          </Form>
        </Container>
      )}
    </>
  );
};
